import { useEffect, useState } from 'react';
import {
  ApplicationStatus,
  ApplicationStep,
  Entity,
} from '../../../types/apiTypes';
import { useScreenView } from '../../../utils/ScreenViewContext';
import SignupFooter from '../SignupFooter';
import { Application } from '../../../types/dbTypes';
import useApi from '../../../utils/AxiosWrapper';
import { HttpStatusCode } from 'axios';
import { useUser } from '../../../utils/UserContext';
import { setApplicationStep } from '../../../pages/signup';
import { ClipLoader } from 'react-spinners';
import { InputNumber, Radio } from 'antd';
import TrustSignals from '../TrustSignals';
import useFrontChat from '../../../hooks/useFrontChat';
import {
  trackMetaCompleteRegistration,
  trackMetaConversionServerSide,
} from '../../../utils/metaConversion';
import {
  AssetPrefill,
  PREFILL_SECTION_ASSETS,
  readAssetPrefill,
  readPrefillSuggestions,
  saveAssetAnswers,
} from '../../../utils/carrierPrefill';
import { usePrimeApplication } from '../../../hooks/useOptimisticApplicationStep';

const { Button: RadioButton } = Radio;

interface BusinessAssetsProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
  application: Application;
  setApplication: (application: Application) => void;
}

function BusinessAssets({
  currStep,
  selectedRole,
  setStep,
  application,
  setApplication,
}: BusinessAssetsProps) {
  // Initialize Front chat widget
  useFrontChat();

  const { screenView } = useScreenView();
  const { userId, refetchUser } = useUser();
  const { callProtectedApi } = useApi();
  const primeApplication = usePrimeApplication(userId);
  const [loading, setLoading] = useState(false);

  /**
   * Truck/trailer counts for the confirmed carrier (THT-711), read once on mount. This step has
   * never touched sessionStorage before; the read is deliberately a lazy initialiser rather
   * than an effect so the counts are the fields' FIRST value and the customer never sees a 0
   * flip to something else under them.
   *
   * Highway's counts are suggestions, and stale ones at that — MCS-150 counts are self-reported
   * biennially and owned/leased/observed rarely agree — so the inputs stay ordinary editable
   * inputs. With no prefill in storage this resolves to an empty object and every default below
   * is the 0 it has always been.
   *
   * What is stored is whatever the fields currently hold, not the original suggestion: the
   * customer can leave this step and come back — a refresh or re-login resumes on the persisted
   * step, and an application parked on a removed step is remapped here — and re-seeding
   * Highway's 12 over the 3 they corrected it to would quietly submit a number they had already
   * rejected. Which is why the write-back below is keyed off the SUGGESTION record rather than
   * the stored counts — the counts are overwritten with the customer's own the first time they
   * touch a field, so they cannot answer "did a prefill ever happen here" a second time.
   */
  const [storedAssets] = useState<AssetPrefill>(() => readAssetPrefill());
  const [wasPrefilled] = useState(
    () => Object.keys(readPrefillSuggestions(PREFILL_SECTION_ASSETS)).length > 0
  );

  // Form state
  const [numTrucks, setNumTrucks] = useState<number>(
    storedAssets.num_trucks ?? 0
  );
  const [numTrailers, setNumTrailers] = useState<number>(
    storedAssets.num_trailers ?? 0
  );

  const [hasOldOrHighMileageTrucks, setHasOldOrHighMileageTrucks] = useState<
    'yes' | 'no'
  >('no');
  const [numOldOrHighMileageTrucks, setNumOldOrHighMileageTrucks] =
    useState<number>(0);
  const [hasInactiveOrReserveTrucks, setHasInactiveOrReserveTrucks] = useState<
    'yes' | 'no'
  >('no');
  const [numInactiveOrReserveTrucks, setNumInactiveOrReserveTrucks] =
    useState<number>(0);

  /**
   * Keep the stored counts in step with the fields, so leaving and re-entering this step brings
   * the customer's own numbers back. Only for a step that was actually prefilled — with no
   * payload nothing is written and the step behaves exactly as it did before this feature.
   */
  useEffect(() => {
    if (!wasPrefilled) return;
    saveAssetAnswers({ num_trucks: numTrucks, num_trailers: numTrailers });
  }, [wasPrefilled, numTrucks, numTrailers]);

  const options = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ];

  const handleSetLoading = (value: boolean) => {
    setLoading(value);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Save asset summary data
      const payload = {
        num_trucks: numTrucks,
        num_trailers: numTrailers,
        has_old_or_high_mileage_trucks: hasOldOrHighMileageTrucks === 'yes',
        num_old_or_high_mileage_trucks:
          hasOldOrHighMileageTrucks === 'yes' ? numOldOrHighMileageTrucks : 0,
        has_inactive_or_reserve_trucks: hasInactiveOrReserveTrucks === 'yes',
        num_inactive_or_reserve_trucks:
          hasInactiveOrReserveTrucks === 'yes' ? numInactiveOrReserveTrucks : 0,
      };

      const response = await callProtectedApi(
        `/applications/upsert`,
        'post',
        {
          application_id: application?.application_id,
          customer_id: userId,
          asset_summary: payload,
        },
        'Error occurred while saving your asset information.'
      );

      if (
        response?.status === HttpStatusCode.Ok ||
        response?.status === HttpStatusCode.Created
      ) {
        // Assets is the last signup step: the Plaid Link step is deprecated, so the application goes
        // straight to pending_review, which also flips the customer to pre-active on the backend.
        // After refetchUser the AuthRouter routes them to the dashboard and its "under review" card.
        const nextStep = await setApplicationStep({
          step: ApplicationStep.PendingReview,
          application_id: application?.application_id,
          customer_id: userId ?? '',
          callProtectedApi,
          setLoading: handleSetLoading,
          previousStep: ApplicationStep.Assets,
          applicationStatus: application?.status as ApplicationStatus,
          /**
           * Hand the dashboard the application this write just returned, so its pending_review card
           * renders on the FIRST paint. Without it the dashboard mounts with no application and
           * shows the wrong onboarding card until /applications/recent answers.
           */
          onApplicationUpdated: primeApplication,
        });
        if (nextStep === ApplicationStep.PendingReview) {
          // Customer has completed the application and moved to pre-active — fire the Meta
          // CompleteRegistration conversion (client-side pixel + ad-blocker-resistant server-side).
          trackMetaCompleteRegistration({
            applicationId: application?.application_id ?? '',
          });
          trackMetaConversionServerSide({
            applicationId: application?.application_id ?? '',
            eventName: 'CompleteRegistration',
            callProtectedApi,
          });

          // The refetch is what moves them: AuthRouter sees the new pre-active status and sends
          // them to the dashboard.
          await refetchUser();
          setStep(ApplicationStep.PendingReview);
        }
      }
    } catch (error: any) {
      console.error('Error submitting asset information:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center">
            {!loading ? (
              <>
                <div className="w-full flex-1 overflow-y-auto py-10 lg:overflow-visible lg:py-20">
                  <div className="flex w-full justify-center px-6 lg:px-12">
                    <div className="mx-auto flex w-auto flex-col items-center justify-center gap-8 lg:flex-row lg:items-start">
                      {/* Form section */}
                      <div className="mx-auto flex-1 lg:w-[800px] lg:max-w-[800px]">
                        <div className="mb-6">
                          <h2 className="sentry-unmask mb-4 text-[30px] font-bold text-primary">
                            Tell Us About Your Fleet
                          </h2>
                          <p className="text-[18px] text-primary">
                            Help us understand your trucks and trailers to
                            better serve your business.
                          </p>
                        </div>

                        <div className="mx-auto flex max-w-[600px] flex-col items-center justify-center space-y-6 lg:max-w-full">
                          {/* Question 1: Number of Trucks */}
                          <div className="flex w-full flex-col items-start justify-start gap-4 rounded-lg border border-bordergray bg-white p-6 shadow-sm">
                            <label className="text-base font-semibold text-primary">
                              How many trucks do you own?{' '}
                              <span className="text-danger">*</span>
                            </label>
                            <InputNumber
                              value={numTrucks}
                              onChange={(value) => setNumTrucks(value || 0)}
                              min={0}
                              size="large"
                              className="w-full [&_.ant-input-number-focused]:!border-accent [&_.ant-input-number-focused]:!shadow-[0_0_0_2px_rgba(247,168,38,0.1)] [&_.ant-input-number]:!rounded-none [&_.ant-input-number]:!shadow-sm [&_.ant-input-number]:hover:!border-accent [&_input]:!h-12"
                              placeholder="0"
                            />
                          </div>

                          {/* Question 2: Number of Trailers */}
                          <div className="flex w-full flex-col items-start justify-start gap-4 rounded-lg border border-bordergray bg-white p-6 shadow-sm">
                            <label className="text-base font-semibold text-primary">
                              How many trailers do you own?{' '}
                              <span className="text-danger">*</span>
                            </label>
                            <InputNumber
                              value={numTrailers}
                              onChange={(value) => setNumTrailers(value || 0)}
                              min={0}
                              size="large"
                              className="w-full [&_.ant-input-number-focused]:!border-accent [&_.ant-input-number-focused]:!shadow-[0_0_0_2px_rgba(247,168,38,0.1)] [&_.ant-input-number]:!rounded-none [&_.ant-input-number]:!shadow-sm [&_.ant-input-number]:hover:!border-accent [&_input]:!h-12"
                              placeholder="0"
                            />
                          </div>

                          {/* Question 3: Old or High Mileage Trucks */}
                          <div className="flex w-full flex-col items-start justify-start gap-6 rounded-lg border border-bordergray bg-white p-6 shadow-sm">
                            <div className="flex flex-col gap-2">
                              <p className="text-base font-semibold text-primary">
                                Do any of your trucks have more than 1,000,000
                                miles OR are greater than 10 years old?
                              </p>
                              <p className="text-sm text-graysecondary">
                                This helps us understand the condition of your
                                fleet
                              </p>
                            </div>
                            <Radio.Group
                              value={hasOldOrHighMileageTrucks}
                              onChange={(e) => {
                                setHasOldOrHighMileageTrucks(e.target.value);
                                if (e.target.value === 'no') {
                                  setNumOldOrHighMileageTrucks(0);
                                }
                              }}
                              className="flex w-full gap-3"
                            >
                              {options.map((option) => (
                                <RadioButton
                                  key={option.value}
                                  value={option.value}
                                  className="!flex !h-12 !min-w-[100px] !items-center !justify-center !rounded-[4px] !border !border-bordergray !bg-white !px-8 !font-medium !text-primary !shadow-sm !transition-all !duration-200 hover:!border-accent hover:!bg-white hover:!text-accent focus:!border-accent focus:!bg-white focus:!text-accent focus:!shadow-none [&.ant-radio-button-wrapper-checked]:!border-accent [&.ant-radio-button-wrapper-checked]:!bg-accent [&.ant-radio-button-wrapper-checked]:!text-white [&.ant-radio-button-wrapper-checked]:!shadow-md [&.ant-radio-button-wrapper-checked]:hover:!border-accent [&.ant-radio-button-wrapper-checked]:hover:!bg-accent [&.ant-radio-button-wrapper::before]:!hidden [&.ant-radio-button-wrapper:focus-within]:!shadow-none"
                                >
                                  {option.label}
                                </RadioButton>
                              ))}
                            </Radio.Group>

                            {/* Conditional follow-up question */}
                            {hasOldOrHighMileageTrucks === 'yes' && (
                              <div className="flex w-full flex-col gap-3 border-t border-bordergray pt-4">
                                <label className="text-sm font-medium text-primary">
                                  How many trucks are over 10 years old OR have
                                  more than 1,000,000 miles?{' '}
                                  <span className="text-danger">*</span>
                                </label>
                                <InputNumber
                                  value={numOldOrHighMileageTrucks}
                                  onChange={(value) =>
                                    setNumOldOrHighMileageTrucks(value || 0)
                                  }
                                  min={0}
                                  max={numTrucks}
                                  size="large"
                                  className="w-full [&_.ant-input-number-focused]:!border-accent [&_.ant-input-number-focused]:!shadow-[0_0_0_2px_rgba(247,168,38,0.1)] [&_.ant-input-number]:!rounded-none [&_.ant-input-number]:!shadow-sm [&_.ant-input-number]:hover:!border-accent [&_input]:!h-12"
                                  placeholder="0"
                                />
                              </div>
                            )}
                          </div>

                          {/* Question 4: Inactive or Reserve Trucks */}
                          <div className="flex w-full flex-col items-start justify-start gap-6 rounded-lg border border-bordergray bg-white p-6 shadow-sm">
                            <div className="flex flex-col gap-2">
                              <p className="text-base font-semibold text-primary">
                                Are any of your trucks inactive or used as
                                "reserve" trucks?
                              </p>
                              <p className="text-sm text-graysecondary">
                                Reserve trucks are backup vehicles not used for
                                regular operations
                              </p>
                            </div>
                            <Radio.Group
                              value={hasInactiveOrReserveTrucks}
                              onChange={(e) => {
                                setHasInactiveOrReserveTrucks(e.target.value);
                                if (e.target.value === 'no') {
                                  setNumInactiveOrReserveTrucks(0);
                                }
                              }}
                              className="flex w-full gap-3"
                            >
                              {options.map((option) => (
                                <RadioButton
                                  key={option.value}
                                  value={option.value}
                                  className="!flex !h-12 !min-w-[100px] !items-center !justify-center !rounded-[4px] !border !border-bordergray !bg-white !px-8 !font-medium !text-primary !shadow-sm !transition-all !duration-200 hover:!border-accent hover:!bg-white hover:!text-accent focus:!border-accent focus:!bg-white focus:!text-accent focus:!shadow-none [&.ant-radio-button-wrapper-checked]:!border-accent [&.ant-radio-button-wrapper-checked]:!bg-accent [&.ant-radio-button-wrapper-checked]:!text-white [&.ant-radio-button-wrapper-checked]:!shadow-md [&.ant-radio-button-wrapper-checked]:hover:!border-accent [&.ant-radio-button-wrapper-checked]:hover:!bg-accent [&.ant-radio-button-wrapper::before]:!hidden [&.ant-radio-button-wrapper:focus-within]:!shadow-none"
                                >
                                  {option.label}
                                </RadioButton>
                              ))}
                            </Radio.Group>

                            {/* Conditional follow-up question */}
                            {hasInactiveOrReserveTrucks === 'yes' && (
                              <div className="flex w-full flex-col gap-3 border-t border-bordergray pt-4">
                                <label className="text-sm font-medium text-primary">
                                  How many trucks are inactive or used as
                                  reserves?{' '}
                                  <span className="text-danger">*</span>
                                </label>
                                <InputNumber
                                  value={numInactiveOrReserveTrucks}
                                  onChange={(value) =>
                                    setNumInactiveOrReserveTrucks(value || 0)
                                  }
                                  min={0}
                                  max={numTrucks}
                                  size="large"
                                  className="w-full [&_.ant-input-number-focused]:!border-accent [&_.ant-input-number-focused]:!shadow-[0_0_0_2px_rgba(247,168,38,0.1)] [&_.ant-input-number]:!rounded-none [&_.ant-input-number]:!shadow-sm [&_.ant-input-number]:hover:!border-accent [&_input]:!h-12"
                                  placeholder="0"
                                />
                              </div>
                            )}
                          </div>

                          <div className="flex h-full w-[60%] flex-col items-center justify-center align-middle">
                            <SignupFooter
                              selectedRole={selectedRole}
                              currStep={currStep}
                              isMobile={screenView === 'mobile'}
                              onSubmitClick={handleSubmit}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Desktop sidebar with trust signals */}
                      {screenView === 'desktop' ? (
                        <div className="sticky top-8 hidden h-fit w-[350px] lg:block">
                          <TrustSignals variant="sidebar" />
                        </div>
                      ) : (
                        <div className="mb-4 w-full max-w-[600px]">
                          <TrustSignals variant="mobile" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center pt-10"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default BusinessAssets;
